/**
 * Design tokens for SmartMixin webapp
 * Centralized design system values for consistent UI
 */

// Weather-specific colors
export const weatherColors = {
  hot: '#f13a59',
  warm: '#FF9800',
  mild: '#FFB800',
  cool: '#33B5E5',
  cold: '#2196F3',
  rain: '#5C9CE6',
  sun: '#FFB800',
  wind: '#8BC34A',
  snow: '#B3E5FC',
  storm: '#7C4DFF',
  fog: '#90A4AE',
  cloud: '#78909C',
};

// Status colors for station connectivity
/**
 * The forecast picker's state strip, per theme.
 *
 * Ported from the grouped-list accent pairs in `apps/mobile/src/theme/index.tsx`
 * so the same state reads the same in both apps. Two tones per family: the fill
 * is the vivid hue already tinted, `On` draws the glyph and the text on it.
 *
 * Measured against their own fill — overridden 4.53:1 light / 5.69:1 dark,
 * following 4.80:1 light / 4.53:1 dark. The vivid hues themselves do not clear
 * 4.5:1, which is why the pair exists rather than one colour.
 */
export const accentColors = {
  light: {
    overriddenFill: '#ebe7d5',
    overriddenOn: '#8b5f00',
    followingFill: '#dadfe7',
    followingOn: '#5e5b7d',
  },
  dark: {
    overriddenFill: '#4b4430',
    overriddenOn: '#f5be4a',
    followingFill: '#3b3f4c',
    followingOn: '#a9a5d9',
  },
} as const;

/**
 * The PRO badge fill.
 *
 * `premium` from `apps/mobile/src/theme/index.tsx`, where it is the same hue in
 * both themes — so it is a flat constant here rather than a palette entry, and
 * no partial mock of this module can drop it.
 *
 * ⚠️ White on this measures **3.83:1**, under the 4.5:1 WCAG AA floor for the
 * 10px bold text it carries. It is shipped at parity with mobile deliberately:
 * two different reds across the apps would be worse than one wrong one, and
 * `#e02348` (4.66:1) fixes both at once whenever that is taken.
 */
export const premiumColor = '#f13a59';

export const statusColors = {
  online: '#4CAF50',
  stale: '#FF9800',
  offline: '#F44336',
};

/**
 * The moon disc, per theme.
 *
 * Ported from `apps/mobile/src/theme/index.tsx` so the two apps draw the same
 * moon. Not derived from the MUI palette: the disc is a depiction of an object,
 * not a surface, and tinting it with `primary` makes it a blue circle.
 *
 * `rim` is load-bearing rather than decorative — without it a full moon
 * dissolves into the light card and a new moon into the dark one. The lit face
 * measures 1.08:1 against the light card, which is exactly the problem; the rim
 * carries 6.00:1 light and 6.23:1 dark and is what makes the disc an object.
 */
export const moonColors = {
  light: {
    lit: '#fdf6e3',
    litEdge: '#cbc3ac',
    dark: '#4a5568',
    ink: '#2d3748',
    rim: '#5b6470',
  },
  dark: {
    lit: '#fdf6e3',
    litEdge: '#c4bca6',
    dark: '#2b3444',
    ink: '#1a2130',
    rim: '#8b97a8',
  },
} as const;

/**
 * The moon card's observing strip.
 *
 * `weatherColors.storm` rather than `primary`: the sun card directly above it
 * already spends the primary tint on its day-length chip, and one colour
 * carrying two meanings in the same column is worse than a second colour. The
 * ink is darkened (light) and lightened (dark) by hand: `#7C4DFF` used as its
 * own ink measures 4.10:1 on the light tint and 3.19:1 on the dark one, and
 * fails AA in both.
 *
 * Measured on the composited tint, not the raw accent: 7.96:1 light, 6.71:1
 * dark.
 */
export const observingColors = {
  light: {background: 'rgba(124, 77, 255, 0.12)', color: '#4a2ea8'},
  dark: {background: 'rgba(124, 77, 255, 0.20)', color: '#b39dff'},
} as const;

/**
 * The year chart's daylight ramp: the colour a day's length is drawn in.
 *
 * `Sun.yearChartGeometry` deliberately returns a 0-1 `fraction` per gradient
 * stop and no colour at all — the ramp is a theme decision and no hex crosses
 * the astral seam in either direction (FE #170). These are mobile's values,
 * which are already tuned against the same `#33B5E5` brand blue, so the two
 * apps draw the same year in the same colours without sharing a constant.
 *
 * One hue at three lightnesses rather than a hue ramp: the chart's whole
 * message is "longer" or "shorter", and a green-to-yellow ramp would read as a
 * change of kind. Dark mode shifts the triple up one step so the curve stays
 * visible against a near-black plot rather than dissolving into it.
 *
 * Never used for text — these fill a 0.35-opacity area and a 2.4px line.
 */
/**
 * The astral surface's accent chip: the day-length chip, the selected day pill
 * and the year pane's headline chip.
 *
 * Hand-tuned, not `primary.dark`: MUI's own derived tone measures 4.16:1 on
 * this tint and fails AA. The light ink is darkened by hand; dark mode uses a
 * lightened tone at 6.77:1. Measured on the composited tint, not the raw
 * accent — the chip background is 12% of `#33B5E5` over the surface, so
 * measuring against the accent itself overstates the ratio.
 *
 * A token because three components now spend it. It was written out three
 * times, each carrying its own copy of the sentence above, which is exactly
 * how the second copy stops being findable.
 */
export const accentChipColors = {
  light: {background: 'rgba(51, 181, 229, 0.12)', color: '#12657f'},
  dark: {background: 'rgba(51, 181, 229, 0.18)', color: '#5bc3ea'},
} as const;

/**
 * The moon's altitude arc: tonight's pass, drawn as a shape.
 *
 * Violet rather than `primary`, which is what mobile uses. The objection is
 * written down four lines above, on `observingColors`: the sun card sits
 * directly above the moon one in the same dashboard column and already spends
 * the `#33B5E5` tint on its day-length chip, and one colour carrying two
 * meanings in the same column is worse than a second colour. The moon surface
 * already owns a violet — `observingColors` is `weatherColors.storm` — so the
 * arc joins it rather than inventing a third hue.
 *
 * `stroke` is a 2px line and `fill` a wash beneath it, so the gate is the 3:1
 * graphics threshold, not 4.5:1. Measured against `background.paper`:
 * `#6d43e0` is 5.95:1 light and `#b39dff` is 8.07:1 dark. Raw `#7C4DFF` is
 * 3.84:1 on the dark paper — passing, but thin at 2px, which is why the dark
 * tone is lightened rather than shared.
 *
 * `horizon` is held to the same 3:1, and is the reason these are not the
 * `divider` token: an altitude is measured *from* the horizon, so the line is
 * meaningful rather than decorative, and outside the up-window the area path
 * has zero height, leaving it on bare paper with nothing to help it. Composited
 * against `background.paper`: `rgba(0,0,0,.42)` is 3.03:1 light and
 * `rgba(255,255,255,.42)` is 4.11:1 dark. The 0.26/0.30 first drafted measured
 * 1.88:1 and 2.67:1 — both failing, on the one line the chart cannot do without.
 *
 * `upWindow` shades the hours the moon is actually above the horizon and is
 * deliberately the faintest thing here: it is context for the curve, not a
 * second series, and it is the only element with no contrast floor to meet.
 */
export const moonArcColors = {
  light: {
    stroke: '#6d43e0',
    fill: 'rgba(124, 77, 255, 0.20)',
    upWindow: 'rgba(124, 77, 255, 0.08)',
    horizon: 'rgba(0, 0, 0, 0.42)',
  },
  dark: {
    stroke: '#b39dff',
    fill: 'rgba(179, 157, 255, 0.22)',
    upWindow: 'rgba(179, 157, 255, 0.10)',
    horizon: 'rgba(255, 255, 255, 0.42)',
  },
} as const;

export const dayCurveColors = {
  light: {low: '#195970', mid: '#227797', high: '#2a96be'},
  dark: {low: '#227797', mid: '#2a96be', high: '#33B5E5'},
} as const;

// Brand colors
export const brandColors = {
  primary: '#33B5E5',
  secondary: '#fb5f26',
};

// Border radius scale
export const borderRadius = {
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  round: 9999,
} as const;

// Shadow definitions
export const shadows = {
  card: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  cardHover: '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
  elevated: '0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)',
  modal: '0px 20px 25px rgba(0, 0, 0, 0.15), 0px 10px 10px rgba(0, 0, 0, 0.05)',
  focus: '0px 0px 0px 3px rgba(51, 181, 229, 0.3)',
};

// Dark mode shadows
export const shadowsDark = {
  card: '0px 1px 3px rgba(0, 0, 0, 0.3), 0px 1px 2px rgba(0, 0, 0, 0.25)',
  cardHover: '0px 4px 6px rgba(0, 0, 0, 0.35), 0px 2px 4px rgba(0, 0, 0, 0.25)',
  elevated: '0px 10px 15px rgba(0, 0, 0, 0.4), 0px 4px 6px rgba(0, 0, 0, 0.3)',
  modal: '0px 20px 25px rgba(0, 0, 0, 0.5), 0px 10px 10px rgba(0, 0, 0, 0.35)',
  focus: '0px 0px 0px 3px rgba(51, 181, 229, 0.4)',
};

// Spacing scale (based on 8px grid)
export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

// Typography scale
export const typography = {
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontFamilyMono: "'JetBrains Mono', 'Fira Code', Consolas, monospace",

  // Font weights
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line heights
  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },

  // Font sizes
  sizes: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    md: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },
};

// Navigation dimensions
export const navigation = {
  railWidth: 72,
  railExpandedWidth: 280,
  railWidthMobile: 56,
  bottomNavHeight: 56,
  appBarHeight: 64,
  appBarHeightMobile: 56,
};

// Breakpoints
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// Z-index scale
export const zIndex = {
  drawer: 1200,
  appBar: 1300,
  modal: 1400,
  tooltip: 1500,
};

// Transitions
export const transitions = {
  fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  spring: '400ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
};

// Temperature color thresholds and colors
export const temperatureScale = {
  freezing: {threshold: 0, color: '#2196F3'},
  cold: {threshold: 10, color: '#33B5E5'},
  cool: {threshold: 15, color: '#4CAF50'},
  mild: {threshold: 20, color: '#8BC34A'},
  warm: {threshold: 25, color: '#FFB800'},
  hot: {threshold: 30, color: '#FF9800'},
  veryHot: {threshold: 35, color: '#f13a59'},
};

/**
 * Get color for temperature value (in Celsius)
 */
export const getTemperatureColor = (tempCelsius: number): string => {
  if (tempCelsius < temperatureScale.freezing.threshold)
    return temperatureScale.freezing.color;
  if (tempCelsius < temperatureScale.cold.threshold)
    return temperatureScale.cold.color;
  if (tempCelsius < temperatureScale.cool.threshold)
    return temperatureScale.cool.color;
  if (tempCelsius < temperatureScale.mild.threshold)
    return temperatureScale.mild.color;
  if (tempCelsius < temperatureScale.warm.threshold)
    return temperatureScale.warm.color;
  if (tempCelsius < temperatureScale.hot.threshold)
    return temperatureScale.hot.color;
  return temperatureScale.veryHot.color;
};

// WBGT heat stress scale (ISO 7243 / Liljegren)
export type WbgtHeatStressLevel = 0 | 1 | 2 | 3 | 4;

export type WbgtHeatStressInfo = {
  category: WbgtHeatStressLevel;
  color: string;
  messageKey:
    | 'heatStressNone'
    | 'heatStressModerate'
    | 'heatStressStrong'
    | 'heatStressVeryStrong'
    | 'heatStressExtreme';
};

export const wbgtHeatStressScale: Record<
  WbgtHeatStressLevel,
  Omit<WbgtHeatStressInfo, 'category'>
> = {
  0: {color: '#4CAF50', messageKey: 'heatStressNone'},
  1: {color: '#FFC107', messageKey: 'heatStressModerate'},
  2: {color: '#FF9800', messageKey: 'heatStressStrong'},
  3: {color: '#F44336', messageKey: 'heatStressVeryStrong'},
  4: {color: '#B71C1C', messageKey: 'heatStressExtreme'},
};

/**
 * Get WBGT heat stress info (category, color, i18n key) from Celsius value
 */
export const getWbgtHeatStressInfo = (
  wbgtCelsius: number,
): WbgtHeatStressInfo => {
  let category: WbgtHeatStressLevel;
  if (wbgtCelsius < 25) category = 0;
  else if (wbgtCelsius < 28) category = 1;
  else if (wbgtCelsius < 29.5) category = 2;
  else if (wbgtCelsius < 31) category = 3;
  else category = 4;

  return {category, ...wbgtHeatStressScale[category]};
};

/**
 * Get status color based on time since last update
 */
export const getStationStatusColor = (minutesSinceUpdate: number): string => {
  if (minutesSinceUpdate <= 30) return statusColors.online;
  if (minutesSinceUpdate <= 240) return statusColors.stale;
  return statusColors.offline;
};

/**
 * Get status label based on time since last update
 */
export const getStationStatusLabel = (
  minutesSinceUpdate: number,
): 'online' | 'stale' | 'offline' => {
  if (minutesSinceUpdate <= 30) return 'online';
  if (minutesSinceUpdate <= 240) return 'stale';
  return 'offline';
};
